import { Alert } from "react-bootstrap";
import DefaultSlide from "../../components/Slides/templates/DefaultSlide";
import ImageSlide from "../../components/Slides/templates/ImageSlide";
import TitleSlide from "../../components/Slides/templates/TitleSlide";
import StudentPicker from "../../components/global/StudentPicker";
import { ISlideObject } from "../../components/Slides/templates/ISlideObject";
import CodeSlide from "../../components/Slides/templates/CodeSlide";
import { useEffect, useRef } from "react";
import "./S04P01.css";
import CodePen from "../../components/Codepen/Codepen";

interface ToastifyOptions {
  text: string;
  duration?: number;
  newWindow?: boolean;
  close?: boolean;
  gravity?: "top" | "bottom";
  position?: "left" | "center" | "right";
  stopOnFocus?: boolean;
  style?: Record<string, string>;
  onClick?: () => void;
}

declare function Toastify(options: ToastifyOptions): { showToast: () => void };

export default function S11P01({ reveal }: ISlideObject) {
  return (
    <>
      <TitleSlide
        title="TI8512 - Desarrollo Ágil de Aplicaciones Web"
        subtitle="Semana 11"
      />
      <DefaultSlide title="Agenda">
        <div style={{ fontSize: "0.75em" }}>
          <ul>
            <li>Introducción a Angular</li>
            <li>Decoradores de TypeScript</li>
            <li>Componentes de Angular</li>
            <li>Servicios</li>
            <li>Directivas</li>
          </ul>
        </div>
      </DefaultSlide>

      <section>
        <TitleSlide
          title="Introducción a Angular"
        />
        <DefaultSlide title="Introducción a Angular">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Framework para construir aplicaciones web de tipo Reactive SPA</li>
              <li>Desarrollado y mantenido por Google desde 2010</li>
              <li>Ofrece una manera estandarizada de estructurar aplicaciones web, gestionar estados, manejar entradas del usuario e interactuar con servicios de back-end</li>
              <li className="fragment">Este si es un framework!</li>
            </ul>
          </div>
        </DefaultSlide>
        <section data-background-color="#161f28">
          <img src="/images/S11/angularversion.jpg" alt="Versiones de angular" />
          <p className="fragment">AngularJS era complicado, lento y feo! <a href="https://codesandbox.io/p/sandbox/angularjs-div-display-guwsc?file=%2Findex.html%3A18%2C33">Ejemplo</a></p>
        </section>
        <section data-background-color="#04427d">
          <img width={"80%"} src="/images/S11/angularvsreact.jpg" alt="Angular VS React" />
        </section>
        <section>
          <p>Aprendamos ahora como crear una aplicación con Angular. El principio de las versiones más recientes de Angular es el mismo que en React: Componentes</p>
          <p>Más sin embargo, los componentes en Angular no son clases que heredan de un React.Component, son clases con decoradores.</p>
        </section>
      </section>

      <section>
        <TitleSlide
          title="Decoradores de TypeScript"
        />
        <DefaultSlide title="Decoradores de TypeScript">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Son una forma de añadir metadatos a clases, métodos, propiedades y parámetros</li>
              <li>Se definen con el símbolo @ seguido del nombre del decorador</li>
              <li>Se pueden usar para modificar el comportamiento de la clase o método al que se aplican</li>
              <li><a href="https://www.typescriptlang.org/docs/handbook/decorators.html" target="_blank" rel="noopener noreferrer">https://www.typescriptlang.org/docs/handbook/decorators.html</a></li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Decoradores de TypeScript"
          language="typescript">
          {`function Log(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function (...args: any[]) {
    console.log(\`Llamando a \${propertyKey} con argumentos: \`, args);
    const result = originalMethod.apply(this, args);
    console.log(\`Resultado: \`, result);
    return result;
  };
}

class Ejemplo {
  @Log
  sumar(a: number, b: number): number {
    return a + b;
  }
}

const ejemplo = new Ejemplo();
ejemplo.sumar(2, 3);`}
        </CodeSlide>
        <section>
          <p>Caso de uso real: cree un decorador que permita llevar una bitácora de quién ha realizado cambios a un objeto en BD.</p>
        </section>
        <CodeSlide
          title="Decorador de bitácora"
          language="typescript">
          {`function Bitacora(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
  const originalMethod = descriptor.value;
  descriptor.value = function (...args: any[]) {
    console.log(\`Usuario: \${this.usuario}, Método: \${propertyKey}, Argumentos: \`, args);
    const result = originalMethod.apply(this, args);
    console.log(\`Resultado: \`, result);
    return result;
  };
}

class BaseDeDatos {
  usuario: string;

  constructor(usuario: string) {
    this.usuario = usuario;
  }

  @Bitacora
  actualizarRegistro(id: number, datos: any) {
    console.log(\`Actualizando registro \${id} con datos: \`, datos);
    return true;
  }
}

const bd = new BaseDeDatos('admin');
bd.actualizarRegistro(1, { nombre: 'Nuevo Nombre' });`}
        </CodeSlide>

        <section>
          <p>Los decoradores son la base fundamental de frameworks como Angular y NestJS</p>
        </section>
      </section>

      <section>
        <TitleSlide
          title="Aprendiendo sobre Angular"
        />
        <DefaultSlide title="Componentes">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Son la unidad básica de construcción de aplicaciones en Angular</li>
              <li>Son clases que definen la lógica y el comportamiento de una parte de la interfaz de usuario</li>
              <li>Se definen con el decorador @Component</li>
              <li>Se pueden anidar para crear jerarquías de componentes</li>
            </ul>
          </div>
        </DefaultSlide>
        <CodeSlide
          title="Ejemplo de componente"
          language="typescript">
          {`import { Component } from '@angular/core';
@Component({
  selector: 'user-profile',
  templateUrl: 'user-profile.html',
  styleUrl: 'user-profile.css',
})
export class UserProfile {
  // Component behavior is defined in here
}`}
        </CodeSlide>
        <section>
          <p>Hora de crear una aplicación de Angular</p>
        </section>
        <DefaultSlide title="Servicios">
          <div style={{ fontSize: "0.75em" }}>
            <ul>
              <li>Son clases que proporcionan funcionalidades específicas y pueden ser compartidas entre diferentes componentes</li>
              <li>Se definen con el decorador @Injectable</li>
              <li>Se pueden inyectar en otros componentes o servicios mediante el constructor</li>
            </ul>
          </div>
        </DefaultSlide>
      </section>






      <ImageSlide image="/images/S06/dancing6.gif" title="Fin de parte 1" />
    </>
  );
}
